import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import GSTSettings from "./GSTSettings";
import ThemeSettings from "./ThemeSettings";
import ReceiptSettings from "./ReceiptSettings";
import PaymentSettings from "./PaymentSettings";
import { WSTabs } from "../../Components/Tabs/WSTabs";
import WSButton from "../../Components/Button/WSButton";
import { Box } from "@mui/material";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { useAppSettings } from "../../Context/AppSettingsContext";

const SettingsPanel: React.FC = () => {
  const { showMessage } = useSnackbar();
  const { settings, updateSettings, loading } = useAppSettings();

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      themeType: "light",
      palette: {
        primary: { main: "", light: "", dark: "", contrastText: "" },
        secondary: { main: "", light: "", dark: "", contrastText: "" },
        success: { main: "", light: "", dark: "", contrastText: "" },
        warning: { main: "", light: "", dark: "", contrastText: "" },
        error: { main: "", light: "", dark: "", contrastText: "" },
        info: { main: "", light: "", dark: "", contrastText: "" },
        background: { default: "", paper: "" },
        text: { primary: "", secondary: "" },
      },

      enableGST: false,
      enableHSNSACCode: false,
      defaultPaymentMethod: "CASH",
      title: "",
      shopName: "",
      shopAddress: "",
      shopPhone: "",
      showCustomerName: true,
      showPhone: true,
      showDiscount: true,
      showPaymentType: true,
      paperSize: "",
      widthPx: 0,
      fontSize: 0,
      headerFontSize: 0,
      templateType: "default",
      footerMessage: "",
    },
  });

  // FIXED: FETCH + COMPLETE RESET WITH NESTED VALUES
  useEffect(() => {
    console.log('settings are', settings)
    if (!settings) return;

    reset({
      themeType: settings?.theme?.themeType ?? "light",
      palette: settings?.theme?.palette ?? {
        // ... your full palette defaults (same as before)
        primary: { main: "", light: "", dark: "", contrastText: "" },
        secondary: { main: "", light: "", dark: "", contrastText: "" },
        success: { main: "", light: "", dark: "", contrastText: "" },
        warning: { main: "", light: "", dark: "", contrastText: "" },
        error: { main: "", light: "", dark: "", contrastText: "" },
        info: { main: "", light: "", dark: "", contrastText: "" },
        background: { default: "", paper: "" },
        text: { primary: "", secondary: "" },
      },
      enableGST: Boolean(settings?.gst?.enableGST),
      enableHSNSACCode: Boolean(settings?.gst?.enableHSNSACCode),
      defaultPaymentMethod: settings?.payment?.defaultPaymentMethod ?? "CASH",
      title: settings?.receiptSetting?.title ?? "",
      shopName: settings?.receiptSetting?.shopName ?? "",
      shopAddress: settings?.receiptSetting?.shopAddress ?? "",
      shopPhone: settings?.receiptSetting?.shopPhone ?? "",
      showCustomerName: Boolean(settings?.receiptSetting?.showCustomerName),
      showPhone: Boolean(settings?.receiptSetting?.showPhone),
      showDiscount: Boolean(settings?.receiptSetting?.showDiscount),
      showPaymentType: Boolean(settings?.receiptSetting?.showPaymentType),
      paperSize: settings?.receiptSetting?.paperSize ?? "",
      widthPx: Number(settings?.receiptSetting?.widthPx) ?? 0,
      fontSize: Number(settings?.receiptSetting?.fontSize) ?? 0,
      headerFontSize: Number(settings?.receiptSetting?.headerFontSize) ?? 0,
      templateType: settings?.receiptSetting?.templateType ?? "default",
      footerMessage: settings?.receiptSetting?.footerMessage ?? "",
    }, { keepDefaultValues: false });

  }, [settings, reset]);

  const onSubmit = async (data: any) => {
    const requestBody = {
      theme: {
        themeType: data.themeType,
        palette: data.palette,
      },
      gst: {
        enableGST: data.enableGST,
        enableHSNSACCode: data.enableHSNSACCode,
      },
      payment: {
        defaultPaymentMethod: data.defaultPaymentMethod,
      },
      receiptSetting: {
        title: data.title,
        shopName: data.shopName,
        shopAddress: data.shopAddress,
        shopPhone: data.shopPhone,
        showCustomerName: data.showCustomerName,
        showPhone: data.showPhone,
        showDiscount: data.showDiscount,
        showPaymentType: data.showPaymentType,
        paperSize: data.paperSize,
        widthPx: data.widthPx,
        fontSize: data.fontSize,
        headerFontSize: data.headerFontSize,
        templateType: data.templateType,
        footerMessage: data.footerMessage,
      },
    };

    try {
      await updateSettings(requestBody)
      showMessage("Settings saved successfully", "success")
    } catch (err) {
      console.error("failed to save settings", err);
      showMessage("failed to save settings", "error")
    }
  };

  const tabs = [
    {
      label: "GST Settings",
      key: "gst",  // ✅ Add unique keys
      content: <GSTSettings watch={watch} setValue={setValue} />,
    },
    {
      label: "Theme Settings",
      key: "theme",
      content: <ThemeSettings control={control} watch={watch} setValue={setValue} />,
    },
    {
      label: "Receipt Settings",
      key: "receipt",  // ✅ Forces ReceiptSettings re-mount after reset
      content: <ReceiptSettings control={control} watch={watch} setValue={setValue} />,
    },
    {
      label: "Payment Settings",
      key: "payment",  // ✅ Forces PaymentSettings re-mount
      content: <PaymentSettings control={control} watch={watch} setValue={setValue} />,
    },
  ];

  return (
    <Box>
      <WSTabs
        id="settings-tabs"
        name="settingsTab"
        control={control}
        setValue={setValue}
        tabs={tabs}
        justifyTabs="flex-start"
      />
      <Box mt={3} display="flex" justifyContent="flex-end">
        <WSButton
          id="saveBtn"
          label="Save Settings"
          color="primary"
          variant="contained"
          size="small"
          loading={loading}
          onClick={handleSubmit(onSubmit)}
        />
      </Box>
    </Box>
  );
};

export default SettingsPanel;